import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import prev from '../../img/home/prev.svg';
import next from '../../img/home/next.svg';

import { Fade } from "react-awesome-reveal";

import SectionTitle from "./SectionTitle";
import SliderLine from "./SliderLine";

import forexImg from "../../img/home/market-forex.webp";
import cryptoImg from "../../img/home/market-crypto.webp";
import stocksImg from "../../img/home/market-stocks.webp";
import indicesImg from "../../img/home/market-indices.webp";
import commoditiesImg from "../../img/home/market-commodities.webp";
import metalsImg from "../../img/home/market-metals.webp";

const MARKETS = [
  {
    img: forexImg,
    badgeKey: "markets.forex.badge",
    item1Key: "markets.forex.item1",
    item2Key: "markets.forex.item2",
  },
  {
    img: cryptoImg,
    badgeKey: "markets.crypto.badge",
    item1Key: "markets.crypto.item1",
    item2Key: "markets.crypto.item2",
  },
  {
    img: stocksImg,
    badgeKey: "markets.stocks.badge",
    item1Key: "markets.stocks.item1",
    item2Key: "markets.stocks.item2",
  },
  {
    img: indicesImg,
    badgeKey: "markets.indices.badge",
    item1Key: "markets.indices.item1",
    item2Key: "markets.indices.item2",
  },
  {
    img: commoditiesImg,
    badgeKey: "markets.commodities.badge",
    item1Key: "markets.commodities.item1",
    item2Key: "markets.commodities.item2",
  },
  {
    img: metalsImg,
    badgeKey: "markets.metals.badge",
    item1Key: "markets.metals.item1",
    item2Key: "markets.metals.item2",
  },
];

// Силуэт ряда из макета: высоты/сдвиги привязаны к КОЛОНКЕ, а не к карточке —
// карточка морфится под позицию, когда приезжает на неё
const COLUMN_PATTERN = [
  { h: 448, o: 0 },
  { h: 316, o: 130 },
  { h: 316, o: 0 },
  { h: 448, o: 0 },
];

// Позиция для дробной колонки (во время свайпа пальцем) — линейная интерполяция
function columnPos(f) {
  const at = (k) => COLUMN_PATTERN[Math.min(COLUMN_PATTERN.length - 1, Math.max(0, k))];
  const k = Math.floor(f);
  const a = at(k);
  const b = at(k + 1);
  const t = f - k;
  return { h: a.h + (b.h - a.h) * t, o: a.o + (b.o - a.o) * t };
}

function applyColumnHeights(swiper) {
  swiper.slides.forEach((slideEl) => {
    const card = slideEl.firstElementChild;
    if (!card) return;
    const pos = columnPos(-slideEl.progress);
    card.style.setProperty("--card-h", `${pos.h}px`);
    card.style.setProperty("--card-offset", `${pos.o}px`);
  });
}

function MarketCard({ item, t }) {
  return (
    <div className="market-card relative overflow-hidden rounded-[20px] bg-[#f2f2f1] h-[316px] sm:h-[360px] lg:h-[var(--card-h,448px)] lg:mt-[var(--card-offset,0px)] lg:transition-[height,margin-top] lg:ease-out">
      <img
        src={item.img}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />

      <div
        className="absolute left-[13px] right-[13px] bottom-[13px] rounded-[15px] p-[12px] flex flex-col gap-[15px]"
        style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(6px)" }}
      >
        <div className="bg-[#bfccd7] inline-flex items-center px-[8px] py-[4px] rounded-full self-start">
          <span className="font-['Inter'] font-normal text-[#1c385f] text-[17px] lg:text-[21px]">
            {t(item.badgeKey)}
          </span>
        </div>
        <ul className="flex flex-col gap-[8px]">
          <li className="font-['Inter'] font-normal text-[15px] lg:text-[18px] text-[#1c385f] list-disc ml-[20px]">
            {t(item.item1Key)}
          </li>
          <li className="font-['Inter'] font-normal text-[15px] lg:text-[18px] text-[#435c7f] list-disc ml-[20px]">
            {t(item.item2Key)}
          </li>
        </ul>
      </div>
    </div>
  );
}

function GlobalMarkets() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [current, setCurrent] = useState(0);

  return (
    <section className="relative z-20" style={{ marginTop: "max(-400px, -26vw)" }}>
      <div className="glcontainer">
        <SectionTitle
          title1={t("markets.title1")}
          title2={t("markets.title2")}
          text={t("markets.subtitle")}
          className="mb-[35px] lg:mb-[50px] mx-auto"
        />

        <Fade triggerOnce direction="up" duration={700}>
        <div className="relative">
          <Swiper
            modules={[Autoplay]}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              applyColumnHeights(swiper);
            }}
            onSlideChange={(swiper) => setCurrent(swiper.realIndex % MARKETS.length)}
            onProgress={applyColumnHeights}
            onSetTransition={(swiper, duration) => {
              swiper.slides.forEach((slideEl) => {
                const card = slideEl.firstElementChild;
                if (card) card.style.transitionDuration = `${duration}ms`;
              });
            }}
            watchSlidesProgress
            spaceBetween={18}
            slidesPerView={1}
            loop
            speed={800}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            grabCursor
            breakpoints={{
              640: { slidesPerView: 2.15, spaceBetween: 20 },
              1024: { slidesPerView: 4, spaceBetween: 30 },
            }}
          >
            {[...MARKETS, ...MARKETS].map((item, i) => (
              <SwiperSlide key={`${item.badgeKey}-${i}`}>
                <MarketCard item={item} t={t} />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Десктопные стрелки — как в макете: в свободной зоне под третьей
              (низкой) карточкой, по нижней линии высоких карточек */}
          <div className="hidden lg:flex absolute bottom-0 right-[calc(25%+8px)] z-10 items-center gap-[20px]">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="hover:scale-[1.05] transition-all"
              aria-label="Previous"
            >
              <img src={prev} alt="Previous" />
            </button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="hover:scale-[1.05] transition-all"
              aria-label="Next"
            >
              <img src={next} alt="Next" />
            </button>
          </div>
        </div>
        </Fade>

        {/* Планшетные стрелки — по центру под слайдером */}
        <div className="hidden sm:flex lg:hidden mt-[28px] items-center justify-center gap-[20px]">
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            className="w-[52px] h-[52px] rounded-full border border-[#1c385f] flex items-center justify-center text-[#1c385f] hover:bg-[#1c385f] hover:text-white transition-all duration-200"
            aria-label="Previous"
          >
            <ArrowLeft strokeWidth={1.5} className="w-[24px] h-[24px]" />
          </button>
          <button
            onClick={() => swiperRef.current?.slideNext()}
            className="w-[52px] h-[52px] rounded-full border border-[#1c385f] flex items-center justify-center text-[#1c385f] hover:bg-[#1c385f] hover:text-white transition-all duration-200"
            aria-label="Next"
          >
            <ArrowRight strokeWidth={1.5} className="w-[24px] h-[24px]" />
          </button>
        </div>

        {/* Мобильная пагинация — линия с 1 / 6 */}
        <div className="sm:hidden">
          <SliderLine
            current={current}
            total={MARKETS.length}
            onPrev={() => swiperRef.current?.slidePrev()}
            onNext={() => swiperRef.current?.slideNext()}
          />
        </div>
      </div>
    </section>
  );
}

export default GlobalMarkets;
